/**
 * System Notifications Service
 * API calls for fetching and managing user-facing system broadcast notifications
 */

import { apiClient } from './api';

/**
 * System notification types
 */
export type NotificationType = 'MAINTENANCE' | 'ANNOUNCEMENT' | 'FEATURE' | 'WARNING' | 'PROMOTION';

/**
 * Notification severity levels
 */
export type NotificationSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';

/**
 * System notification object
 */
export interface SystemNotification {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  severity: NotificationSeverity;
  isDismissible: boolean;
  isDismissed: boolean;
  ctaText: string | null;
  ctaUrl: string | null;
  startAt: string | null;
  endAt: string | null;
  createdAt: string;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * System Notifications Service
 */
export const systemNotificationsService = {
  /**
   * Get all active notifications for the current user
   * Returns notifications that:
   * - Are active (isActive = true)
   * - Are within their time window (startAt <= now <= endAt)
   * - Includes dismissed notifications with isDismissed: true flag
   */
  getActiveNotifications: async (): Promise<SystemNotification[]> => {
    const response = await apiClient.get<ApiResponse<SystemNotification[]>>(
      '/api/system-notifications/active'
    );
    return response.data.data;
  },

  /**
   * Dismiss a notification for the current user
   * Creates a dismissal record so the notification won't be shown again
   * @param id - Notification ID to dismiss
   */
  dismissNotification: async (id: number): Promise<void> => {
    await apiClient.post<ApiResponse<void>>(`/api/system-notifications/${id}/dismiss`);
  },
};
